import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { downloadCSV, downloadFile, openPdfInNewTab } from "@shared/utils/utils";
import {
  downloadBillTemplate,
  exportBillList,
  getBillCycleFrequency,
  getBillCycleList,
  getBillList,
  getBillSummary,
  getPremiseList,
} from "./api";
import { useState } from "react";
import { toast } from "@shared/ui/use-toast";

export const useSummary = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billMaster.billSummary(), queryParams],
    () => getBillSummary(queryParams),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      enabled: !!queryParams,
    }
  );
};

export const useBillList = (
  queryParams?: Record<string, any>,
  page?: number
) => {
  console.log("filters are:", queryParams);
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billMaster.billList(), queryParams, page],
    () => getBillList(queryParams, page),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      enabled: !!queryParams,
    }
  );
};

export const useBillListExport = (
  queryParams: Record<string, any> = {},
  page?: number
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    [QueryKeyFactory.module.bx.billMaster.exportList(), queryParams, page],
    async () => {
      setIsExporting(true);
      try {
        const blob = await exportBillList(queryParams, page);
        
    toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export bills: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await downloadBillTemplate(billId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const useViewBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const useBillCycleFrequency = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billMaster.billCycleFrequency()],
    () => getBillCycleFrequency()
  );
};

export const usePremiseList = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billMaster.premiseList()],
    () => getPremiseList()
  );
};

export const useBillCycleList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.billMaster.billCycle(),
    () => getBillCycleList(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};
